/**
 * Utilities for parsing and downloading DOCX files
 * embedded in chat messages as base64 data.
 *
 * The backend returns a special marker in the message content:
 * <!--DOCX_DOWNLOAD:{"filename": "...", "data": "base64..."}-->
 *
 * This is used by workflows that output Word documents:
 * - Bid generation workflow
 * - Any future workflow with DOCX output
 */

import { logger } from "@/telemetry";

export interface DocxDownloadData {
  filename: string;
  data: string;
}

/**
 * Regex pattern to match the DOCX download marker in message content.
 * The marker format is: <!--DOCX_DOWNLOAD:{"filename": "...", "data": "..."}-->
 */
export const DOCX_DOWNLOAD_REGEX = /<!--DOCX_DOWNLOAD:(\{.*?\})-->/;

/**
 * Parses the DOCX download marker from message content.
 * Returns the filename and base64 data if found, null otherwise.
 *
 * @param messageContent - The raw message content from the backend
 * @returns DocxDownloadData if marker is found and valid, null otherwise
 */
export function parseDocxDownload(messageContent: string): DocxDownloadData | null {
  if (!messageContent || typeof messageContent !== "string") {
    return null;
  }

  const match = messageContent.match(DOCX_DOWNLOAD_REGEX);

  if (!match || !match[1]) {
    return null;
  }

  try {
    const parsed = JSON.parse(match[1]);

    // Validate required fields
    if (
      typeof parsed.filename !== "string" ||
      !parsed.filename ||
      typeof parsed.data !== "string" ||
      !parsed.data
    ) {
      logger.error("DOCX download data missing required fields:", undefined, { parsed });

      return null;
    }

    return {
      filename: parsed.filename,
      data: parsed.data,
    };
  } catch (e) {
    logger.error("Failed to parse DOCX download data:", e);

    return null;
  }
}

/**
 * Removes the DOCX download marker from message content for display.
 * Only removes the marker itself - does not trim the entire message to preserve
 * any intentional leading/trailing whitespace in the original content.
 *
 * @param messageContent - The raw message content
 * @returns Message content with the marker removed
 */
export function removeDocxDownloadMarker(messageContent: string): string {
  if (!messageContent || typeof messageContent !== "string") {
    return messageContent;
  }

  return messageContent.replace(DOCX_DOWNLOAD_REGEX, "");
}

/**
 * Downloads a DOCX file from base64 data.
 *
 * @param filename - The filename for the download
 * @param base64Data - The base64-encoded DOCX file data
 */
export function downloadDocxFile(filename: string, base64Data: string): void {
  try {
    // Decode base64 to binary
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob with DOCX MIME type
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    URL.revokeObjectURL(url);
  } catch (e) {
    logger.error("Failed to download DOCX file:", e);
    throw new Error("Failed to download the document file. Please try again.");
  }
}
